import React from "react";

const INITIALISM_LABELS = {
  api: "API",
  bol: "BOL",
  eta: "ETA",
  gps: "GPS",
  id: "ID",
  llm: "LLM",
  pod: "POD",
  tms: "TMS",
  url: "URL",
  uuid: "UUID",
};

const ROOT_FIELD_PRIORITY = [
  "load_number",
  "load_id",
  "trip_id",
  "status",
  "event_time",
  "driver",
  "pickup",
  "delivery",
  "equipment",
  "customer",
  "document",
  "invoice",
  "dry_run",
  "allow_actions",
  "synthetic_test",
  "test_run_id",
];

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function isInternalIdentifierKey(value) {
  const normalized = String(value ?? "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[-\s]+/g, "_")
    .toLowerCase();

  return (
    normalized === "id" ||
    normalized === "uuid" ||
    normalized.endsWith("_id") ||
    normalized.endsWith("_uuid")
  );
}

export function parseStructuredValue(value) {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (
    !(
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    )
  ) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

export function humanizeFieldName(value) {
  const words = String(value ?? "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return words
    .map((word) => {
      const normalized = word.toLowerCase();
      if (INITIALISM_LABELS[normalized]) return INITIALISM_LABELS[normalized];
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    })
    .join(" ");
}

function isIsoDate(value) {
  return (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) &&
    !Number.isNaN(Date.parse(value))
  );
}

function isEmail(value) {
  return (
    typeof value === "string" &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
  );
}

function isPhoneField(fieldName, value) {
  return (
    typeof value === "string" &&
    /phone|mobile|telephone/i.test(fieldName || "") &&
    /^\+?[\d\s().-]{7,}$/.test(value)
  );
}

function isUrl(value) {
  if (typeof value !== "string") return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function isStructured(value) {
  const parsed = parseStructuredValue(value);
  return isPlainObject(parsed) || (
    Array.isArray(parsed) &&
    parsed.some((item) => isPlainObject(parseStructuredValue(item)))
  );
}

function sortRootEntries(entries) {
  const priority = new Map(
    ROOT_FIELD_PRIORITY.map((field, index) => [field, index]),
  );
  return [...entries].sort(([left], [right]) => {
    const leftPriority = priority.get(left) ?? Number.MAX_SAFE_INTEGER;
    const rightPriority = priority.get(right) ?? Number.MAX_SAFE_INTEGER;
    if (leftPriority !== rightPriority) return leftPriority - rightPriority;
    return 0;
  });
}

export function flattenStructuredRows(value, path = []) {
  const parsed = parseStructuredValue(value);

  if (isPlainObject(parsed)) {
    const allEntries = Object.entries(parsed);
    if (allEntries.length === 0) {
      return [{ path, fieldName: path.at(-1) || "", value: null }];
    }
    const entries = allEntries.filter(
      ([key]) => !isInternalIdentifierKey(key),
    );
    return entries.flatMap(([key, child]) =>
      flattenStructuredRows(child, [...path, key]),
    );
  }

  if (Array.isArray(parsed)) {
    if (parsed.length === 0) {
      return [{ path, fieldName: path.at(-1) || "", value: [] }];
    }
    if (!parsed.some((item) => isStructured(item))) {
      return [{ path, fieldName: path.at(-1) || "", value: parsed }];
    }
    return parsed.flatMap((item, index) =>
      flattenStructuredRows(item, [...path, `item_${index + 1}`]),
    );
  }

  return [{ path, fieldName: path.at(-1) || "", value: parsed }];
}

function displayLabel(path) {
  return path.map(humanizeFieldName).join(" · ") || "Value";
}

function StructuredValue({ value, fieldName }) {
  const parsed = parseStructuredValue(value);

  if (parsed === null || parsed === undefined || parsed === "") {
    return <span className="text-muted-foreground">—</span>;
  }

  if (typeof parsed === "boolean") {
    return (
      <span
        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
          parsed
            ? "bg-success-surface text-success-foreground"
            : "bg-surface-hover text-content-secondary"
        }`}
      >
        {parsed ? "Yes" : "No"}
      </span>
    );
  }

  if (Array.isArray(parsed)) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {parsed.map((item, index) => (
          <span
            key={`${String(item)}-${index}`}
            className="rounded bg-surface-hover px-2 py-0.5 text-[11px] text-content-secondary"
          >
            {String(item)}
          </span>
        ))}
      </div>
    );
  }

  if (isIsoDate(parsed)) {
    return (
      <span className="text-content-secondary">
        {new Date(parsed).toLocaleString()}
      </span>
    );
  }

  if (isEmail(parsed)) {
    return (
      <a
        href={`mailto:${parsed}`}
        className="text-info-foreground hover:text-info-solid hover:underline"
      >
        {parsed}
      </a>
    );
  }

  if (isPhoneField(fieldName, parsed)) {
    return (
      <a
        href={`tel:${parsed}`}
        className="text-info-foreground hover:text-info-solid hover:underline"
      >
        {parsed}
      </a>
    );
  }

  if (isUrl(parsed)) {
    return (
      <a
        href={parsed}
        target="_blank"
        rel="noreferrer"
        className="text-info-foreground hover:text-info-solid hover:underline break-all"
      >
        {parsed}
      </a>
    );
  }

  if (typeof parsed === "number") {
    return <span className="text-content-secondary">{parsed.toLocaleString()}</span>;
  }

  return <span className="text-content-secondary break-words">{String(parsed)}</span>;
}

function DataRows({ rows }) {
  return (
    <tbody>
      {rows.map((row, index) => (
        <tr
          key={`${row.path.join(".")}-${index}`}
          className="border-b border-border/70 last:border-0"
        >
          <th
            scope="row"
            className="w-[38%] px-3 py-2 text-left align-top text-[11px] font-medium text-muted-foreground"
          >
            {displayLabel(row.path)}
          </th>
          <td className="px-3 py-2 align-top text-xs">
            <StructuredValue value={row.value} fieldName={row.fieldName} />
          </td>
        </tr>
      ))}
    </tbody>
  );
}

function DataTable({ title, rows }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border/70 bg-card">
      {title && (
        <div className="border-b border-border/70 bg-surface-hover px-3 py-2">
          <h4 className="text-xs font-medium text-content-secondary">{title}</h4>
        </div>
      )}
      <table className="w-full table-fixed">
        <caption className="sr-only">{title || "Structured data"}</caption>
        <DataRows rows={rows} />
      </table>
    </div>
  );
}

export default function StructuredDataTable({
  data,
  rawLabel = "View raw event data",
  showRaw = false,
}) {
  const parsed = parseStructuredValue(data);
  if (!isPlainObject(parsed)) {
    return (
      <DataTable
        rows={flattenStructuredRows(parsed, ["value"])}
      />
    );
  }

  const entries = sortRootEntries(
    Object.entries(parsed).filter(
      ([key]) => !isInternalIdentifierKey(key),
    ),
  );
  const scalarRows = entries
    .filter(([, value]) => !isStructured(value))
    .flatMap(([key, value]) => flattenStructuredRows(value, [key]));
  const groupedEntries = entries
    .filter(([, value]) => isStructured(value))
    .map(([key, value]) => [key, flattenStructuredRows(value)])
    .filter(([, rows]) => rows.length > 0);

  return (
    <div className="space-y-3">
      {scalarRows.length > 0 && (
        <DataTable
          title="Event details"
          rows={scalarRows}
        />
      )}

      {groupedEntries.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {groupedEntries.map(([key, rows]) => (
            <DataTable
              key={key}
              title={humanizeFieldName(key)}
              rows={rows}
            />
          ))}
        </div>
      )}

      {showRaw && (
        <details className="group">
          <summary className="cursor-pointer text-[11px] text-muted-foreground hover:text-content-secondary">
            {rawLabel}
          </summary>
          <pre className="mt-2 max-h-48 overflow-auto rounded-lg border border-border/60 bg-card p-3 text-[10px] text-muted-foreground">
            {JSON.stringify(parsed, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
